import React, { useState, useEffect } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { getAuth } from "firebase/auth";
import { useHistory } from "react-router-dom";
import cogoToast from "cogo-toast";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import Header from "components/Navbar.js";
import { APP_NAME } from "../constants";
import MarketplaceSelector from "components/coop/MarketplaceSelector.js";
import ProductSummary from "components/coop/ProductSummary.js";
import {
    fetchCoBuyGroups,
    updateCoBuyGroup,
    deleteCoBuyGroup,
    createCoBuyInterest
} from "DBServices/firestoreDBService";
import useServedLocations from "hooks/useServedLocations";
import CoBuyPaymentForm from "components/coop/CoBuyPaymentForm";

const Container = tw.div`relative -mx-8 mt-0 bg-gray-100 min-h-screen`;
const ContentContainer = tw.div`max-w-screen-xl mx-auto py-16 px-4`;
const HeadingSection = tw.div`flex justify-between items-center mb-8 flex-wrap gap-4`;
const Title = tw.h1`text-3xl sm:text-4xl font-extrabold text-green-900`;
const Subtitle = tw.p`text-sm text-gray-600 max-w-xl mt-2`;

const ActionBtn = styled.button`
  ${tw`text-white font-bold py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition duration-200 text-sm focus:outline-none`}
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
`;

const Banner = styled.div`
  ${tw`text-white rounded-2xl p-8 mb-10 shadow-xl flex flex-col md:flex-row justify-between items-center gap-6`}
  background: linear-gradient(135deg, #064e3b 0%, #115e59 100%);
  border: 1px solid rgba(6, 78, 59, 0.1);
`;
const BannerText = tw.div`flex-1`;
const BannerTitle = tw.h2`text-2xl font-bold mb-2`;
const BannerDesc = styled.p`
  ${tw`text-sm leading-relaxed`}
  color: #d1fae5;
`;
const StepContainer = styled.div`
  ${tw`grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 text-center border-t pt-6`}
  border-color: rgba(209, 250, 229, 0.2);
`;
const Step = tw.div`text-xs`;

const StepNum = styled.div`
  ${tw`text-gray-900 w-8 h-8 rounded-full flex items-center justify-center font-black mx-auto mb-2 text-sm shadow-md`}
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
`;

const SearchGrid = tw.div`flex flex-wrap gap-4 mb-8 bg-white p-4 rounded-xl shadow-sm items-center justify-between border border-gray-200`;
const FilterSelect = tw.select`border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-green-600 bg-white text-gray-700 transition`;
const SearchInput = tw.input`border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-green-600 w-full sm:w-64 text-gray-700 transition`;

const GroupsGrid = tw.div`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8`;

const GroupCard = styled.div`
  ${tw`bg-white rounded-xl shadow-sm p-6 flex flex-col justify-between border border-gray-200 transition duration-300`}
  &:hover {
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    transform: translateY(-4px);
  }
`;
const GroupHeader = tw.div`flex justify-between items-start mb-4`;
const GroupBadge = styled.span`
  ${tw`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border`}
  ${props => {
        if (props.status === 'active') return `background-color: #d1fae5; color: #065f46; border-color: rgba(6, 95, 70, 0.2);`;
        if (props.status === 'completed') return `background-color: #dbeafe; color: #1e40af; border-color: rgba(30, 64, 175, 0.2);`;
        if (props.status === 'ordered') return `background-color: #fef3c7; color: #92400e; border-color: rgba(146, 64, 14, 0.2);`;
        return `background-color: #f3f4f6; color: #374151; border-color: rgba(55, 65, 81, 0.2);`;
    }}
`;
const GroupTitle = tw.h3`text-lg font-bold text-gray-800 mb-1`;
const GroupNeighborhood = tw.p`text-xs font-bold text-green-700 mb-4 flex items-center gap-1`;
const ProgressSection = tw.div`mb-4`;
const ProgressBarBackground = tw.div`w-full bg-gray-100 h-2 rounded-full overflow-hidden mt-2`;
const ProgressBarFill = styled.div`
  ${tw`h-full rounded-full transition-all duration-500`}
  background: linear-gradient(to right, #10b981, #14b8a6);
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.4);
  width: ${props => props.width}%;
`;
const InfoRow = tw.div`flex justify-between text-xs text-gray-600 py-3 border-b border-gray-100`;
const AvatarGroup = tw.div`flex items-center gap-1 mt-4`;
const AvatarCircle = styled.div`
  ${tw`w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-xs font-black -ml-2 first:ml-0 shadow-sm`}
  background-color: #d1fae5;
  color: #065f46;
  z-index: ${props => 10 - props.idx};
`;

// Modal Overlays
const ModalOverlay = tw.div`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4`;
const ModalBody = tw.div`bg-white rounded-xl p-8 w-full max-w-3xl max-h-screen overflow-y-auto shadow-2xl`;
const ModalTitle = tw.h2`text-xl font-bold mb-6 text-gray-800`;
const FormLabel = tw.label`block text-sm font-semibold text-gray-600 mb-1`;
const FormInput = tw.input`w-full border border-gray-300 rounded-lg px-4 py-2 mb-4 text-sm focus:outline-none focus:border-green-500`;
const FormSelect = tw.select`w-full border border-gray-300 rounded-lg px-4 py-2 mb-4 text-sm focus:outline-none focus:border-green-500 bg-white`;
const FormTextarea = tw.textarea`w-full border border-gray-300 rounded-lg px-4 py-2 mb-4 text-sm focus:outline-none focus:border-green-500 h-20`;

// Chat component elements inside group details
const ChatContainer = tw.div`border border-gray-200 rounded-xl p-4 bg-gray-100 mb-4 flex flex-col h-56`;
const MessageList = tw.div`flex-1 overflow-y-auto space-y-2 mb-2 pr-1`;
const MsgBubble = styled.div`
  ${tw`p-3 rounded-lg text-xs max-w-xs shadow-sm`}
  ${props => props.self 
    ? `
      background: linear-gradient(135deg, #059669 0%, #047857 100%);
      color: #ffffff;
      margin-left: auto;
      border-top-right-radius: 0;
    ` 
    : `
      background-color: #ffffff;
      color: #1a202c;
      border: 1px solid #edf2f7;
      margin-right: auto;
      border-top-left-radius: 0;
    `
  }
`;
const MsgSender = tw.span`block font-bold text-gray-500 text-xs mb-1`;
const ChatInputRow = tw.div`flex gap-2`;
const ChatInput = tw.input`flex-1 border border-gray-300 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-green-600 focus:bg-white bg-white transition duration-150`;
const SendBtn = tw.button`bg-green-600 hover:bg-green-700 text-white text-xs font-bold px-4 rounded-lg transition duration-150`;

const EMPTY_GROUP = { name: "", state: "", neighborhood: "Ikeja", dropoffAddress: "", targetAmount: "", maxMembers: "", bulkItem: "Rice Sacks" };
const EMPTY_INTEREST = { 
    name: "", 
    state: "",
    neighborhood: "Ikeja", 
    dropoffAddress: "", 
    targetAmount: "", 
    maxMembers: "", 
    bulkItem: "Foodstuffs",
    groupType: "fixed",
    contributionAmount: "",
    portionRequested: "",
    marketplaceProducts: []
};

export default function CoBuyHub() {
    const auth = getAuth();
    const history = useHistory();
    const [user, setUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);

    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [stateFilter, setStateFilter] = useState("all");
    const [areaFilter, setAreaFilter] = useState("all");

    // Modal management
    const [createOpen, setCreateOpen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [newGroupForm, setNewGroupForm] = useState(EMPTY_INTEREST);
    const [selectedGroup, setSelectedGroup] = useState(null);

    // Join contribution inputs
    const [contribAmount, setContribAmount] = useState("");
    const [contribItems, setContribItems] = useState("");
    const [newMsg, setNewMsg] = useState("");
    const [payForMemberAmount, setPayForMemberAmount] = useState("");
    const [payForMemberId, setPayForMemberId] = useState("");

    // Marketplace grid dialog (creation form)
    const [marketplaceOpen, setMarketplaceOpen] = useState(false);

    // Marketplace grid dialog (group detail: add/request foods)
    const [detailMarketplaceOpen, setDetailMarketplaceOpen] = useState(false);

    // Payment modal for joining a group
    const [paymentOpen, setPaymentOpen] = useState(false);

    useEffect(() => {
        const unsub = auth.onAuthStateChanged((u) => {
            setUser(u);
            setAuthLoading(false);
        });
        return unsub;
    }, [auth]);

    // Admin-managed served states & their neighborhoods (state → neighborhoods)
    const { states, neighborhoodsByState, allNeighborhoods } = useServedLocations();
    const selectedNeighborhoods = newGroupForm.state ? (neighborhoodsByState[newGroupForm.state] || []) : [];
    const neighborhoodOptions = selectedNeighborhoods.length > 0 ? selectedNeighborhoods : allNeighborhoods;

    const loadGroups = async () => {
        setLoading(true);
        try {
            const data = await fetchCoBuyGroups();
            setGroups(data);
        } catch (e) {
            console.error("CoBuyHub load error:", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadGroups();
    }, []);

    const handleCreateGroup = async (e) => {
        e.preventDefault();
        if (!user) {
            cogoToast.info("Please log in to show interest in a co-op group");
            history.push("/login");
            return;
        }

        // Calculate total from marketplace products if any are selected
        let calculatedTotal = Number(newGroupForm.targetAmount);
        if (newGroupForm.marketplaceProducts && newGroupForm.marketplaceProducts.length > 0) {
            calculatedTotal = newGroupForm.marketplaceProducts.reduce((sum, product) => {
                return sum + ((product.unitPrice || 0) * (product.quantity || 0));
            }, 0);
        }

        const newInterest = {
            ...newGroupForm,
            groupType: newGroupForm.groupType || "fixed",
            targetAmount: calculatedTotal,
            status: "pending",
            submittedBy: user.uid,
            submittedByName: user.displayName || user.email.split("@")[0],
            submittedByEmail: user.email,
            submittedAt: new Date().toISOString()
        };

        try {
            await createCoBuyInterest(newInterest);
            cogoToast.success("Your interest has been submitted for review! The admin will review and approve it shortly.");
            setCreateOpen(false);
            setNewGroupForm(EMPTY_INTEREST);
        } catch (err) {
            cogoToast.error("Submission failed: " + err.message);
        }
    };

    const handleSendMessage = async () => {
        if (!newMsg.trim() || !user || !selectedGroup) return;
        const msg = {
            userId: user.uid,
            userName: user.displayName || user.email.split("@")[0],
            text: newMsg,
            timestamp: new Date().toISOString()
        };
        const updatedMsgs = [...(selectedGroup.messages || []), msg];
        try {
            await updateCoBuyGroup(selectedGroup.firestoreId, { messages: updatedMsgs });
            setSelectedGroup(prev => ({ ...prev, messages: updatedMsgs }));
            setNewMsg("");
            // Update local list silently
            setGroups(prev => prev.map(g => g.firestoreId === selectedGroup.firestoreId ? { ...g, messages: updatedMsgs } : g));
        } catch (err) {
            cogoToast.error("Failed to send message: " + err.message);
        }
    };

    const handleJoinGroup = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!user) {
            cogoToast.info("Please log in to join a group");
            history.push("/login");
            return;
        }
        if (!contribAmount || Number(contribAmount) <= 0) {
            alert("Please enter a valid amount");
            return;
        }

        if (selectedGroup.members.some(m => m.userId === user.uid)) {
            cogoToast.info("You've already joined this group co-op!");
            return;
        }

        // Check if group has expired
        if (selectedGroup.expiryDate) {
            const expiryDate = new Date(selectedGroup.expiryDate);
            if (expiryDate < new Date()) {
                cogoToast.error("This co-op group has expired and is no longer accepting new members");
                return;
            }
        }

        // Open payment modal — the actual join is done in handlePaymentSuccess
        setPaymentOpen(true);
    };

    // Called by CoBuyPaymentForm on successful payment
    const handleJoinPaymentSuccess = async ({ amount }) => {
        setPaymentOpen(false);
        const newMember = {
            userId: user.uid,
            userName: user.displayName || user.email.split("@")[0],
            userEmail: user.email,
            amount: Number(amount || contribAmount),
            items: []
        };
        const updatedMembers = [...(selectedGroup.members || []), newMember];
        const newTotal = Number(selectedGroup.currentAmount || 0) + Number(amount || contribAmount);
        const autoCompleted = selectedGroup.targetAmount && newTotal >= selectedGroup.targetAmount ? "completed" : selectedGroup.status;
        try {
            const updatePayload = { members: updatedMembers, currentAmount: newTotal, status: autoCompleted };
            await updateCoBuyGroup(selectedGroup.firestoreId, updatePayload);
            cogoToast.success("Successfully joined the co-op pool!");
            setSelectedGroup(prev => ({ ...prev, ...updatePayload }));
            setContribAmount("");
            setContribItems("");
            loadGroups();
        } catch (err) {
            cogoToast.error("Join pool failed: " + err.message);
        }
    };

    const handlePayForMember = async (e) => {
        e.preventDefault();
        if (!user) {
            cogoToast.info("Please log in to pay for a member");
            history.push("/login");
            return;
        }
        if (!payForMemberAmount || Number(payForMemberAmount) <= 0) {
            alert("Please enter a valid amount");
            return;
        }
        if (!payForMemberId) {
            alert("Please select a member to pay for");
            return;
        }

        // Check if user is a member of this group
        if (!selectedGroup.members.some(m => m.userId === user.uid)) {
            cogoToast.error("You must be a member of this group to pay for someone");
            return;
        }

        const targetMember = selectedGroup.members.find(m => m.userId === payForMemberId);
        if (!targetMember) {
            alert("Selected member not found");
            return;
        }

        // Update the member's amount
        const updatedMembers = selectedGroup.members.map(m => {
            if (m.userId === payForMemberId) {
                return {
                    ...m,
                    amount: m.amount + Number(payForMemberAmount),
                    paidByOthers: [...(m.paidByOthers || []), {
                        paidBy: user.uid,
                        paidByName: user.displayName || user.email.split("@")[0],
                        amount: Number(payForMemberAmount),
                        paidAt: new Date().toISOString()
                    }]
                };
            }
            return m;
        });

        const newTotal = Number(selectedGroup.currentAmount || 0) + Number(payForMemberAmount);
        const autoCompleted = newTotal >= selectedGroup.targetAmount ? "completed" : selectedGroup.status;

        try {
            const updatePayload = {
                members: updatedMembers,
                currentAmount: newTotal,
                status: autoCompleted
            };

            await updateCoBuyGroup(selectedGroup.firestoreId, updatePayload);
            
            // Send a message to the group
            const msg = {
                userId: "system",
                userName: "System",
                text: `${user.displayName || user.email.split("@")[0]} paid ₦${Number(payForMemberAmount).toLocaleString()} for ${targetMember.userName}`,
                timestamp: new Date().toISOString()
            };
            const updatedMsgs = [...(selectedGroup.messages || []), msg];
            await updateCoBuyGroup(selectedGroup.firestoreId, { messages: updatedMsgs });
            
            cogoToast.success(`Successfully paid ₦${Number(payForMemberAmount).toLocaleString()} for ${targetMember.userName}!`);

            setSelectedGroup(prev => ({ ...prev, ...updatePayload, messages: updatedMsgs }));
            setPayForMemberAmount("");
            setPayForMemberId("");
            loadGroups();
        } catch (err) {
            cogoToast.error("Payment failed: " + err.message);
        }
    };

    if (authLoading) return <div style={{ textAlign: "center", paddingTop: "5rem" }}>Loading Hub...</div>;

    const filtered = groups.filter(g => {
        const matchesState = stateFilter === "all" || g.state === stateFilter;
        const matchesArea = areaFilter === "all" || g.neighborhood === areaFilter;
        const matchesSearch = !search.trim() || g.name.toLowerCase().includes(search.toLowerCase()) || (g.bulkItem || '').toLowerCase().includes(search.toLowerCase());
        return matchesState && matchesArea && matchesSearch;
    });

    return (
        <>
        <AnimationRevealPage>
            <Header />
            <Container>
                <ContentContainer>
                    <HeadingSection>
                        <div>
                            <Title>{APP_NAME} Co-Op Corner</Title>
                            <Subtitle>Pool bulk food purchases and logistics costs together with neighbors in your neighborhood.</Subtitle>
                        </div>
                        {user ? (
                            <ActionBtn onClick={() => setCreateOpen(true)}>💡 Show Interest</ActionBtn>
                        ) : (
                            <ActionBtn onClick={() => { cogoToast.info("Sign in to show interest"); history.push("/login"); }}>Log in to Show Interest</ActionBtn>
                        )}
                    </HeadingSection>

                    <Banner>
                        <BannerText>
                            <BannerTitle>💡 How Proximity Co-Buying Works</BannerTitle>
                            <BannerDesc>
                                Food items (rice sacks, pepper baskets, oil kegs) are significantly cheaper in wholesale bulk but too large for single households. By joining forces, we divide bulk orders and split the shipping/logistics cost to a central drop-off spot in your neighborhood.
                            </BannerDesc>
                            <StepContainer>
                                <Step>
                                    <StepNum>1</StepNum>
                                    <strong>Join or Create</strong>
                                    <p style={{ opacity: 0.85, fontSize: 10 }}>Start/Find a group in your area</p>
                                </Step>
                                <Step>
                                    <StepNum>2</StepNum>
                                    <strong>Fund Pool</strong>
                                    <p style={{ opacity: 0.85, fontSize: 10 }}>Chip in contribution shares</p>
                                </Step>
                                <Step>
                                    <StepNum>3</StepNum>
                                    <strong>Bulk Delivery</strong>
                                    <p style={{ opacity: 0.85, fontSize: 10 }}>Arrives at central drop-off</p>
                                </Step>
                            </StepContainer>
                        </BannerText>
                    </Banner>

                    <SearchGrid>
                        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                            <FilterSelect value={stateFilter} onChange={e => { const v = e.target.value; setStateFilter(v); setAreaFilter("all"); }}>
                                <option value="all">All States</option>
                                {states.map(s => (
                                    <option key={s.firestoreId || s.name} value={s.name}>{s.name}</option>
                                ))}
                            </FilterSelect>
                            <FilterSelect value={areaFilter} onChange={e => setAreaFilter(e.target.value)}>
                                <option value="all">All Neighborhoods</option>
                                {(stateFilter === "all" ? allNeighborhoods : (neighborhoodsByState[stateFilter] || [])).map(n => (
                                    <option key={n} value={n}>{n} Proximity</option>
                                ))}
                            </FilterSelect>
                            <SearchInput
                                type="text"
                                placeholder="Search foods or pools..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                        <div style={{ fontSize: "0.85rem", color: "#6b7280" }}>
                            Found {filtered.length} active co-ops
                        </div>
                    </SearchGrid>

                    {loading ? (
                        <div style={{ textAlign: "center", color: "#9ca3af", padding: "4rem" }}>Syncing neighborhood co-op groups...</div>
                    ) : (
                        <GroupsGrid>
                            {filtered.length === 0 ? (
                                <div style={{ gridColumn: "span 3", textAlign: "center", fontStyle: "italic", color: "#9ca3af", padding: "4rem" }}>
                                    No active co-buying pools found in this neighborhood. Be the first to start one!
                                </div>
                            ) : (
                                filtered.map((g) => {
                                    const grandTotal = (g.marketplaceProducts || []).reduce((s, p) => s + (Number(p.unitPrice) || 0) * (Number(p.quantity) || 0), 0) || Number(g.targetAmount) || 0;
                                    const pct = Math.min(100, Math.round(((g.currentAmount || 0) / (grandTotal || 1)) * 100));
                                    return (
                                        <GroupCard key={g.firestoreId}>
                                            <div>
                                                <GroupHeader>
                                                    <GroupTitle>{g.name}</GroupTitle>
                                                    <GroupBadge status={g.status}>{g.status}</GroupBadge>
                                                </GroupHeader>
                                                <GroupNeighborhood>
                                                    <svg style={{ width: 14, height: 14 }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                                    {g.neighborhood} Proximity
                                                </GroupNeighborhood>

                                                <ProgressSection>
                                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", fontWeight: 600 }}>
                                                        <span style={{ color: "#4b5563" }}>Goal Funding</span>
                                                        <span style={{ color: "#10b981" }}>₦{(g.currentAmount || 0).toLocaleString()} / ₦{grandTotal.toLocaleString()} ({pct}%)</span>
                                                    </div>
                                                    <ProgressBarBackground>
                                                        <ProgressBarFill width={pct} />
                                                    </ProgressBarBackground>
                                                </ProgressSection>

                                                <InfoRow>
                                                    <span>Drop-off Spot:</span>
                                                    <strong style={{ color: "#374151" }}>{g.dropoffAddress}</strong>
                                                </InfoRow>
                                                <InfoRow>
                                                    <span>Bulk Product:</span>
                                                    <strong style={{ color: "#10b981" }}>{g.bulkItem}</strong>
                                                </InfoRow>
                                                <InfoRow>
                                                    <span>Max Members:</span>
                                                    <strong style={{ color: "#374151" }}>{g.maxMembers ? `${g.maxMembers} limit` : "Unlimited"}</strong>
                                                </InfoRow>
                                                {g.expiryDate && (
                                                    <InfoRow>
                                                        <span>Expires:</span>
                                                        <strong style={{ 
                                                            color: new Date(g.expiryDate) < new Date() ? "#dc2626" : "#f59e0b",
                                                            fontSize: "0.75rem"
                                                        }}>
                                                            {new Date(g.expiryDate).toLocaleDateString()} {new Date(g.expiryDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                            {new Date(g.expiryDate) < new Date() && " (Expired)"}
                                                        </strong>
                                                    </InfoRow>
                                                )}
                                                {g.expectedDeliveryDate && (
                                                    <InfoRow>
                                                        <span>Expected Delivery:</span>
                                                        <strong style={{ color: "#3b82f6", fontSize: "0.75rem" }}>
                                                            {new Date(g.expectedDeliveryDate).toLocaleDateString()}
                                                        </strong>
                                                    </InfoRow>
                                                )}
                                            </div>

                                            <div>
                                                <AvatarGroup>
                                                    {g.members?.map((m, idx) => (
                                                        <AvatarCircle key={idx} idx={idx} title={m.userName}>
                                                            {m.userName?.substring(0, 2).toUpperCase()}
                                                        </AvatarCircle>
                                                    ))}
                                                    <span style={{ fontSize: "0.7rem", color: "#6b7280", marginLeft: 8 }}>
                                                        {g.members?.length || 0} joined
                                                    </span>
                                                </AvatarGroup>

                                                <button
                                                    onClick={() => { setSelectedGroup(g); setDetailsOpen(true); }}
                                                    style={{ width: "100%", marginTop: "1rem", backgroundColor: "#e2e8f0", hover: { backgroundColor: "#cbd5e1" }, color: "#334155", fontWeight: "bold", padding: "0.5rem", borderRadius: "6px", fontSize: "0.8rem", cursor: "pointer", transition: "0.2s" }}
                                                >
                                                    {g.members?.some(m => m.userId === user?.uid) ? "View Co-Op Hub / Chat" : "Join Pool & Chat"}
                                                </button>
                                            </div>
                                        </GroupCard>
                                    );
                                })
                            )}
                        </GroupsGrid>
                    )}
                </ContentContainer>
            </Container>
        </AnimationRevealPage>

            {/* CREATE CO-OP INTEREST MODAL */}
            {createOpen && (
                <ModalOverlay onClick={() => setCreateOpen(false)}>
                    <ModalBody onClick={e => e.stopPropagation()}>
                        <ModalTitle>Show Interest in Starting a Co-Buying Group</ModalTitle>
                        <p style={{ fontSize: "0.85rem", color: "#6b7280", marginBottom: "1rem" }}>
                            Submit your interest and an admin will review and approve it to create the group or add you to an existing one.
                        </p>
                        <form onSubmit={handleCreateGroup}>
                            <FormLabel>Co-Op Group Name *</FormLabel>
                            <FormInput
                                value={newGroupForm.name}
                                onChange={e => { const v = e.target.value; setNewGroupForm(prev => ({ ...prev, name: v })); }}
                                placeholder="e.g. Surulere Rice and Grains Pool"
                                required
                            />

                            <FormLabel>Group Type *</FormLabel>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                                {[
                                    {
                                        value: "fixed",
                                        icon: "🔒",
                                        title: "Fixed Co-Op",
                                        desc: "A fixed pool built up front from the creator's items (top priority, locked). New items can only join via admin-approved requests."
                                    },
                                    {
                                        value: "dynamic",
                                        icon: "🌊",
                                        title: "Dynamic Pool",
                                        desc: "An open food pool. Add any foods — your share is based on the food you add & pay for, and you receive exactly the food you contributed for."
                                    }
                                ].map(opt => {
                                    const active = newGroupForm.groupType === opt.value;
                                    return (
                                        <div
                                            key={opt.value}
                                            onClick={() => setNewGroupForm(prev => ({ ...prev, groupType: opt.value }))}
                                            style={{
                                                cursor: "pointer",
                                                border: active ? "2px solid #10b981" : "2px solid #e5e7eb",
                                                background: active ? "#ecfdf5" : "#fff",
                                                borderRadius: "12px",
                                                padding: "0.9rem",
                                                transition: "all 0.15s"
                                            }}
                                        >
                                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.35rem" }}>
                                                <span style={{ fontSize: "1.1rem" }}>{opt.icon}</span>
                                                <strong style={{ color: active ? "#065f46" : "#111827" }}>{opt.title}</strong>
                                                {active && (
                                                    <span style={{ marginLeft: "auto", background: "#10b981", color: "#fff", borderRadius: "999px", fontSize: "0.65rem", padding: "2px 8px", fontWeight: 700 }}>Selected</span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: "0.74rem", color: "#6b7280", lineHeight: 1.4 }}>{opt.desc}</div>
                                        </div>
                                    );
                                })}
                            </div>
                            <p style={{ fontSize: "0.72rem", color: "#6b7280", marginBottom: "0.5rem" }}>
                                {newGroupForm.groupType === "fixed"
                                    ? "Pick specific marketplace products. These become the curated pool; they can't change unless you request edits the admin approves."
                                    : "Add any foods to the pool. Your contribution share is based on the food you add and pay for — you receive exactly the food you contributed for."}
                            </p>

                            <div>
                                <FormLabel>State *</FormLabel>
                                <FormSelect
                                    value={newGroupForm.state}
                                    onChange={e => { const v = e.target.value; setNewGroupForm(prev => ({ ...prev, state: v, neighborhood: "" })); }}
                                    required
                                >
                                    <option value="">Select a state</option>
                                    {states.map(s => (
                                        <option key={s.firestoreId || s.name} value={s.name}>{s.name}</option>
                                    ))}
                                </FormSelect>
                            </div>

                            <div style={{ display: "flex", gap: "1rem" }}>
                                <div style={{ flex: 1 }}>
                                    <FormLabel>Neighborhood Area *</FormLabel>
                                    <FormSelect
                                        value={newGroupForm.neighborhood}
                                        onChange={e => { const v = e.target.value; setNewGroupForm(prev => ({ ...prev, neighborhood: v })); }}
                                        required
                                    >
                                        <option value="">
                                            {selectedNeighborhoods.length > 0 ? "Select a neighborhood" : (states.length > 0 ? "Select a state first" : "No areas available")}
                                        </option>
                                        {neighborhoodOptions.map(n => (
                                            <option key={n} value={n}>{n}</option>
                                        ))}
                                    </FormSelect>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <FormLabel>Bulk Item Pool Category</FormLabel>
                                    <FormSelect
                                        value={newGroupForm.bulkItem}
                                        onChange={e => { const v = e.target.value; setNewGroupForm(prev => ({ ...prev, bulkItem: v })); }}
                                    >
                                        <option value="Foodstuffs">Foodstuffs</option>
                                    </FormSelect>
                                </div>
                            </div>

                            {/* Marketplace product picker — fixed (curated) and dynamic (open pool) groups */}
                            {(newGroupForm.groupType === "fixed" || newGroupForm.groupType === "dynamic") && (
                                <div style={{ marginBottom: "1rem" }}>
                                    <FormLabel>Marketplace Products (Optional)</FormLabel>
                                    <button
                                        type="button"
                                        onClick={() => setMarketplaceOpen(true)}
                                        style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.6rem 1.2rem", border: "2px dashed #10b981", borderRadius: "8px", background: "#f0fdf4", color: "#065f46", fontWeight: "bold", fontSize: "0.85rem", cursor: "pointer", width: "100%", justifyContent: "center" }}
                                    >
                                        🛒 Browse &amp; Select from Marketplace
                                        {newGroupForm.marketplaceProducts?.length > 0 && (
                                            <span style={{ background: "#10b981", color: "white", borderRadius: "999px", padding: "1px 8px", fontSize: "0.75rem" }}>
                                                {newGroupForm.marketplaceProducts.length} selected
                                            </span>
                                        )}
                                    </button>
                                    {newGroupForm.marketplaceProducts?.length > 0 && (
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.5rem" }}>
                                            {newGroupForm.marketplaceProducts.map(p => (
                                                <span key={p.productId} style={{ display: "flex", alignItems: "center", gap: 4, background: "#d1fae5", color: "#065f46", fontSize: "0.72rem", fontWeight: 600, padding: "3px 10px", borderRadius: "999px" }}>
                                                    {p.productName} · {p.quantity} {p.measureUnit}
                                                    <button type="button" onClick={() => setNewGroupForm(prev => ({ ...prev, marketplaceProducts: prev.marketplaceProducts.filter(x => x.productId !== p.productId) }))} style={{ background: "none", border: "none", cursor: "pointer", color: "#059669", fontWeight: "bold", fontSize: "0.85rem", lineHeight: 1, padding: 0 }}>×</button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div style={{ display: "flex", gap: "1rem" }}>
                                {(newGroupForm.groupType === "fixed" || newGroupForm.groupType === "dynamic") && (
                                    <div style={{ flex: 1 }}>
                                        <FormLabel>Total Cost Goal (₦)</FormLabel>
                                        <div
                                            style={{
                                                padding: "0.6rem 0.8rem",
                                                border: "1px solid #e5e7eb",
                                                borderRadius: "8px",
                                                background: "#f8fafc",
                                                fontWeight: 600,
                                                color: "#111827"
                                            }}
                                        >
                                            ₦{(
                                                newGroupForm.marketplaceProducts && newGroupForm.marketplaceProducts.length > 0
                                                    ? newGroupForm.marketplaceProducts.reduce((sum, product) => sum + ((product.unitPrice || 0) * (product.quantity || 0)), 0)
                                                    : 0
                                            ).toLocaleString()}
                                            {newGroupForm.marketplaceProducts?.length > 0 && (
                                                <span style={{ fontSize: "0.7rem", color: "#10b981", marginLeft: "0.5rem" }}>✓ from selected products</span>
                                            )}
                                        </div>
                                    </div>
                                )}
                                <div style={{ flex: 1 }}>
                                    <FormLabel>Member Limit (Optional)</FormLabel>
                                    <FormInput
                                        type="number"
                                        value={newGroupForm.maxMembers}
                                        onChange={e => { const v = e.target.value; setNewGroupForm(prev => ({ ...prev, maxMembers: v })); }}
                                        placeholder="Unlimited"
                                    />
                                </div>
                            </div>

                            <FormLabel>Neighborhood Central Drop-off Site Address *</FormLabel>
                            <FormTextarea
                                value={newGroupForm.dropoffAddress}
                                onChange={e => { const v = e.target.value; setNewGroupForm(prev => ({ ...prev, dropoffAddress: v })); }}
                                placeholder="Specify a coordinator's house or accessible drop-off spot in this area"
                                required
                            />

                            <div style={{ backgroundColor: "#eff6ff", padding: "1rem", borderRadius: "8px", marginBottom: "1rem", border: "1px solid #bfdbfe" }}>
                                <div style={{ fontSize: "0.85rem", fontWeight: "bold", color: "#1e40af", marginBottom: "0.75rem" }}>
                                    Your Initial Contribution (Optional but Recommended)
                                </div>
                                <p style={{ fontSize: "0.75rem", color: "#6b7280", marginBottom: "0.75rem" }}>
                                    Provide your contribution details to automatically join as the first member when approved.
                                </p>
                                <div style={{ display: "flex", gap: "1rem" }}>
                                    <div style={{ flex: 1 }}>
                                        <FormLabel style={{ color: "#1e40af" }}>Your Contribution Amount (₦)</FormLabel>
                                        <FormInput
                                            type="number"
                                            value={newGroupForm.contributionAmount}
                                            onChange={e => { const v = e.target.value; setNewGroupForm(prev => ({ ...prev, contributionAmount: v })); }}
                                            placeholder="e.g. 5000"
                                            style={{ marginBottom: 0, border: "1px solid #bfdbfe" }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end", marginTop: "1rem" }}>
                                <button type="button" onClick={() => setCreateOpen(false)} style={{ backgroundColor: "#e2e8f0", color: "#475569", padding: "0.6rem 1.2rem", borderRadius: "8px", fontWeight: "bold", border: "none", cursor: "pointer", fontSize: "0.85rem" }}>Cancel</button>
                                <button type="submit" style={{ backgroundColor: "#10b981", color: "white", padding: "0.6rem 1.2rem", borderRadius: "8px", fontWeight: "bold", border: "none", cursor: "pointer", fontSize: "0.85rem" }}>Submit Interest</button>
                            </div>
                        </form>
                    </ModalBody>
                </ModalOverlay>
            )}

            {/* VIEW GROUP CO-OP DETAILS & neighborhood chat MODAL */}
            {detailsOpen && selectedGroup && (
                <ModalOverlay onClick={() => { setDetailsOpen(false); setSelectedGroup(null); }}>
                    <ModalBody onClick={e => e.stopPropagation()} style={{ maxWidth: "600px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "1rem" }}>
                            <div>
                                <ModalTitle style={{ marginBottom: 2 }}>{selectedGroup.name}</ModalTitle>
                                <span style={{ fontSize: "0.75rem", color: "#10b981", fontWeight: 600 }}>
                                    {selectedGroup.neighborhood} Neighborhood{selectedGroup.state ? `, ${selectedGroup.state}` : ""} Pool
                                </span>
                            </div>
                            <GroupBadge status={selectedGroup.status}>{selectedGroup.status}</GroupBadge>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", backgroundColor: "#f8fafc", padding: "1rem", borderRadius: "8px", marginBottom: "1.5rem", fontSize: "0.8rem" }}>
                            <div>
                                <span style={{ color: "#64748b" }}>Bulk Item: </span>
                                <strong>{selectedGroup.bulkItem}</strong>
                            </div>
                            <div>
                                <span style={{ color: "#64748b" }}>Drop Site: </span>
                                <strong>{selectedGroup.dropoffAddress}</strong>
                            </div>
                            <div>
                                <span style={{ color: "#64748b" }}>Progress: </span>
                                <strong>₦{(selectedGroup.currentAmount || 0).toLocaleString()} / ₦{(selectedGroup.targetAmount || 0).toLocaleString()}</strong>
                            </div>
                            <div>
                                <span style={{ color: "#64748b" }}>Slots filled: </span>
                                <strong>{selectedGroup.members?.length || 0} / {selectedGroup.maxMembers ? selectedGroup.maxMembers : "Unlimited"}</strong>
                            </div>
                        </div>

                        {/* Display marketplace products if any */}
                        {selectedGroup.marketplaceProducts && selectedGroup.marketplaceProducts.length > 0 && (
                            <div style={{ marginBottom: "1.5rem" }}>
                                <ProductSummary products={selectedGroup.marketplaceProducts} showRemove={false} />
                            </div>
                        )}

                        {/* Add foods (dynamic) or request items (fixed) */}
                        {selectedGroup.status === "active" && user && (
                            <div style={{ marginBottom: "1.5rem" }}>
                                {selectedGroup.groupType === "dynamic" ? (
                                    <button
                                        type="button"
                                        onClick={() => setDetailMarketplaceOpen(true)}
                                        style={{ width: "100%", backgroundColor: "#10b981", color: "white", padding: "0.6rem", borderRadius: "8px", fontWeight: "bold", border: "none", cursor: "pointer", fontSize: "0.85rem" }}
                                    >
                                        ➕ Add Foods to Pool
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => setDetailMarketplaceOpen(true)}
                                        style={{ width: "100%", backgroundColor: "#f59e0b", color: "white", padding: "0.6rem", borderRadius: "8px", fontWeight: "bold", border: "none", cursor: "pointer", fontSize: "0.85rem" }}
                                    >
                                        🔧 Request to Add Items (admin approval)
                                    </button>
                                )}
                                {selectedGroup.groupType === "fixed" && (selectedGroup.pendingItemRequests || []).some(r => r.status === "pending") && (
                                    <p style={{ fontSize: "0.75rem", color: "#92400e", marginTop: "0.5rem" }}>
                                        ⏳ {(selectedGroup.pendingItemRequests || []).filter(r => r.status === "pending").length} item request(s) awaiting admin approval.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Chat section for neighborhood co-op feeling */}
                        <div style={{ fontWeight: 600, fontSize: "0.85rem", marginBottom: "0.5rem", color: "#475569" }}>Co-Op Community Chat</div>
                        <ChatContainer>
                            <MessageList>
                                {selectedGroup.messages && selectedGroup.messages.length > 0 ? (
                                    selectedGroup.messages.map((m, idx) => (
                                        <MsgBubble key={idx} self={m.userId === user?.uid}>
                                            {m.userId !== user?.uid && <MsgSender>{m.userName}</MsgSender>}
                                            {m.text}
                                        </MsgBubble>
                                    ))
                                ) : (
                                    <div style={{ color: "#94a3b8", fontStyle: "italic", textAlign: "center", marginTop: "2rem" }}>No messages yet. Greet your co-op neighbors!</div>
                                )}
                            </MessageList>
                            {user && (
                                <ChatInputRow>
                                    <ChatInput
                                        type="text"
                                        placeholder="Ask a question or sync drop-off time..."
                                        value={newMsg}
                                        onChange={e => setNewMsg(e.target.value)}
                                        onKeyDown={e => { if (e.key === "Enter") handleSendMessage(); }}
                                    />
                                    <SendBtn onClick={handleSendMessage}>Send</SendBtn>
                                </ChatInputRow>
                            )}
                        </ChatContainer>

                        {/* Current co-op members list */}
                        <div style={{ fontWeight: 600, fontSize: "0.85rem", marginBottom: "0.5rem", color: "#475569" }}>Co-op Contributors ({selectedGroup.members?.length || 0})</div>
                        <div style={{ maxHeight: "120px", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "0.5rem 1rem", marginBottom: "1.5rem", fontSize: "0.8rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                            {selectedGroup.members && selectedGroup.members.length > 0 ? (
                                selectedGroup.members.map((m, idx) => (
                                    <div key={idx} style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #f1f5f9", paddingBottom: "4px" }}>
                                        <div>
                                            <strong>{m.userName}</strong>
                                            {m.items && m.items.length > 0 && <span style={{ color: "#64748b", marginLeft: "10px" }}>({m.items[0].title})</span>}
                                        </div>
                                        <span style={{ fontWeight: 600, color: "#10b981" }}>₦{m.amount.toLocaleString()}</span>
                                    </div>
                                ))
                            ) : (
                                <div style={{ color: "#94a3b8", fontStyle: "italic", textAlign: "center", padding: "1rem" }}>No contributors yet. Join first!</div>
                            )}
                        </div>

                        {/* Contribution split pay zone */}
                        {selectedGroup.status === "active" && !selectedGroup.members.some(m => m.userId === user?.uid) && (
                            <div style={{ border: "2px dashed #10b981", borderRadius: "8px", padding: "1rem", backgroundColor: "#f0fdf4", marginBottom: "1rem" }}>
                                <div style={{ fontWeight: "bold", fontSize: "0.85rem", color: "#166534", marginBottom: "0.75rem" }}>Chip in & Join this Proximity Pool</div>
                                <form onSubmit={handleJoinGroup}>
                                    <div style={{ display: "flex", gap: "1rem" }}>
                                        <div style={{ flex: 1 }}>
                                            <FormLabel style={{ color: "#166534" }}>Your Contribution Share (₦) *</FormLabel>
                                            <FormInput
                                                type="number"
                                                placeholder="e.g. 5000"
                                                value={contribAmount}
                                                onChange={e => setContribAmount(e.target.value)}
                                                style={{ marginBottom: 0, border: "1px solid #bbf7d0" }}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <button
                                        type="submit"
                                        style={{ width: "100%", marginTop: "1rem", backgroundColor: "#10b981", color: "white", padding: "0.6rem", borderRadius: "6px", fontWeight: "bold", border: "none", cursor: "pointer", fontSize: "0.85rem" }}
                                    >
                                        Fund Contribution & Join Co-Op Pool
                                    </button>
                                </form>
                            </div>
                        )}

                        {/* Unconditional Pay for someone (for members only) */}
                        {selectedGroup.status === "active" && selectedGroup.members.some(m => m.userId === user?.uid) && (
                            <div style={{ border: "2px dashed #3b82f6", borderRadius: "8px", padding: "1rem", backgroundColor: "#eff6ff", marginBottom: "1rem" }}>
                                <div style={{ fontWeight: "bold", fontSize: "0.85rem", color: "#1e40af", marginBottom: "0.75rem" }}>💙 Unconditional Pay for a Fellow Member</div>
                                <form onSubmit={handlePayForMember}>
                                    <div style={{ display: "flex", gap: "1rem", flexDirection: "column" }}>
                                        <div>
                                            <FormLabel style={{ color: "#1e40af" }}>Select Member *</FormLabel>
                                            <FormSelect
                                                value={payForMemberId}
                                                onChange={e => setPayForMemberId(e.target.value)}
                                                style={{ marginBottom: 0, border: "1px solid #bfdbfe" }}
                                                required
                                            >
                                                <option value="">-- Choose a member --</option>
                                                {selectedGroup.members.filter(m => m.userId !== user?.uid).map((m, idx) => (
                                                    <option key={idx} value={m.userId}>{m.userName} ({m.userEmail})</option>
                                                ))}
                                            </FormSelect>
                                        </div>
                                        <div>
                                            <FormLabel style={{ color: "#1e40af" }}>Amount to Pay (₦) *</FormLabel>
                                            <FormInput
                                                type="number"
                                                placeholder="e.g. 2000"
                                                value={payForMemberAmount}
                                                onChange={e => setPayForMemberAmount(e.target.value)}
                                                style={{ marginBottom: 0, border: "1px solid #bfdbfe" }}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <button
                                        type="submit"
                                        style={{ width: "100%", marginTop: "1rem", backgroundColor: "#3b82f6", color: "white", padding: "0.6rem", borderRadius: "6px", fontWeight: "bold", border: "none", cursor: "pointer", fontSize: "0.85rem" }}
                                    >
                                        Pay for Member
                                    </button>
                                </form>
                            </div>
                        )}

                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1.5rem" }}>
                            <button
                                onClick={() => { setDetailsOpen(false); setSelectedGroup(null); }}
                                style={{ backgroundColor: "#64748b", color: "white", padding: "0.5rem 1.5rem", borderRadius: "6px", fontWeight: "bold", border: "none", cursor: "pointer", fontSize: "0.8rem" }}
                            >
                                Close View
                            </button>
                        </div>
                    </ModalBody>
                </ModalOverlay>
            )}
            {marketplaceOpen && (
                <MarketplaceSelector
                    selectedProducts={newGroupForm.marketplaceProducts || []}
                    onDone={(products) => {
                        // Bulk item category is "Foodstuffs" only; products are picked
                        // individually so their own categories apply. No need to map to a
                        // generic bulk label here.
                        setNewGroupForm(prev => ({ ...prev, marketplaceProducts: products, bulkItem: "Foodstuffs" }));
                        setMarketplaceOpen(false);
                    }}
                    onClose={() => setMarketplaceOpen(false)}
                />
            )}

            {detailMarketplaceOpen && selectedGroup && (
                <MarketplaceSelector
                    selectedProducts={selectedGroup.marketplaceProducts || []}
                    onDone={async (products) => {
                        setDetailMarketplaceOpen(false);
                        try {
                            if (selectedGroup.groupType === "dynamic") {
                                await updateCoBuyGroup(selectedGroup.firestoreId, { marketplaceProducts: products });
                                setSelectedGroup(prev => ({ ...prev, marketplaceProducts: products }));
                                cogoToast.success("Foods added to the pool!");
                            } else {
                                const request = {
                                    requestedBy: user?.uid,
                                    requestedByName: user?.displayName || user?.email?.split("@")[0] || "Member",
                                    requestedByEmail: user?.email,
                                    items: products,
                                    requestedAt: new Date().toISOString(),
                                    status: "pending"
                                };
                                const pending = [...(selectedGroup.pendingItemRequests || []), request];
                                await updateCoBuyGroup(selectedGroup.firestoreId, { pendingItemRequests: pending });
                                setSelectedGroup(prev => ({ ...prev, pendingItemRequests: pending }));
                                cogoToast.success("Item request submitted for admin approval.");
                            }
                            loadGroups();
                        } catch (err) {
                            cogoToast.error("Could not save selection: " + (err.message || err));
                        }
                    }}
                    onClose={() => setDetailMarketplaceOpen(false)}
                />
            )}

            {paymentOpen && selectedGroup && (
                <CoBuyPaymentForm
                    groupName={selectedGroup.name}
                    amount={contribAmount}
                    user={user}
                    items={
                        selectedGroup.marketplaceProducts && selectedGroup.marketplaceProducts.length > 0
                            ? selectedGroup.marketplaceProducts.map(p => ({
                                title: p.productName,
                                quantity: p.quantity || 1,
                                price: p.unitPrice || 0,
                                total: (p.unitPrice || 0) * (p.quantity || 0),
                                imageSrc: p.productImage || "",
                                basket: []
                            }))
                            : []
                    }
                    onSuccess={handleJoinPaymentSuccess}
                    onCancel={() => setPaymentOpen(false)}
                />
            )}
        </>
    );
}
